import { DEVICE_ID_PATTERN, HLC_PATTERN, MAX_HLC_DRIFT_MS } from '../model/constants';

/**
 * Hybrid logical clock timestamps (data dictionary §5):
 * `{ms: 12 hex}-{counter: 4 hex}-{deviceId: 8 hex}`, compared as strings; larger is newer.
 */

export const HLC_MAX_COUNTER = 0xffff;
export const HLC_MAX_MS = 2 ** 48 - 1;

export interface ParsedHlc {
  readonly ms: number;
  readonly counter: number;
  readonly deviceId: string;
}

export function isHlc(value: string): boolean {
  return HLC_PATTERN.test(value);
}

export function formatHlc({ ms, counter, deviceId }: ParsedHlc): string {
  if (!Number.isInteger(ms) || ms < 0 || ms > HLC_MAX_MS) {
    throw new RangeError(`HLC milliseconds out of range: ${ms}`);
  }
  if (!Number.isInteger(counter) || counter < 0 || counter > HLC_MAX_COUNTER) {
    throw new RangeError(`HLC counter out of range: ${counter}`);
  }
  if (!DEVICE_ID_PATTERN.test(deviceId)) throw new RangeError(`Invalid device id: ${deviceId}`);
  return `${ms.toString(16).padStart(12, '0')}-${counter.toString(16).padStart(4, '0')}-${deviceId}`;
}

export function parseHlc(value: string): ParsedHlc {
  if (!isHlc(value)) throw new RangeError(`Invalid HLC: ${value}`);
  return {
    ms: parseInt(value.slice(0, 12), 16),
    counter: parseInt(value.slice(13, 17), 16),
    deviceId: value.slice(18),
  };
}

/** The "zero instant" HLC used for system-generated records: any user edit is newer. */
export function zeroHlc(deviceId: string): string {
  return formatHlc({ ms: 0, counter: 0, deviceId });
}

/**
 * The "system" HLC, just above the zero instant: for deletions made by the system (purging an
 * expired draft). It beats the zero-time records the system generates, and every user write
 * beats it, so a system deletion never overrides a user's edit on another device.
 */
export function systemHlc(deviceId: string): string {
  return formatHlc({ ms: 0, counter: 1, deviceId });
}

/** The wall-clock milliseconds of a timestamp: the last modification instant of its record. */
export function hlcMillis(value: string): number {
  return parseHlc(value).ms;
}

/** True for any timestamp before the first real instant (zero and system timestamps). */
export function isZeroHlc(value: string): boolean {
  return parseHlc(value).ms === 0;
}

export function isSystemHlc(value: string): boolean {
  const { ms, counter } = parseHlc(value);
  return ms === 0 && counter === 1;
}

/** True when the timestamp lies more than `maxDriftMs` ahead of the wall clock `nowMs`. */
export function isHlcBeyondDrift(
  value: string,
  nowMs: number,
  maxDriftMs: number = MAX_HLC_DRIFT_MS,
): boolean {
  return parseHlc(value).ms > Math.floor(nowMs) + maxDriftMs;
}

/**
 * The largest timestamp that is not beyond the drift limit of the wall clock `nowMs`: every
 * timestamp up to it passes `isHlcBeyondDrift`, every larger one fails it.
 */
export function latestHlcWithinDrift(nowMs: number, maxDriftMs: number = MAX_HLC_DRIFT_MS): string {
  return formatHlc({
    ms: Math.min(Math.floor(nowMs) + maxDriftMs, HLC_MAX_MS),
    counter: HLC_MAX_COUNTER,
    deviceId: 'ffffffff',
  });
}

/** Orders by wall time, then counter, then device (string order of the fixed-width format). */
export function compareHlc(a: string, b: string): -1 | 0 | 1 {
  if (a === b) return 0;
  return a < b ? -1 : 1;
}

export function newerHlc(a: string, b: string): string {
  return compareHlc(a, b) >= 0 ? a : b;
}

/** Random 8-hex-digit device identifier. */
export function newDeviceId(): string {
  const bytes = globalThis.crypto.getRandomValues(new Uint8Array(4));
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
}
